import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { isEmpty } from '@node-space/utils'
import { usePostConvertQuoteMutation } from 'hooks/mutations/usePostConvertQuoteMutation'
import { usePutAcceptQuoteMutation } from 'hooks/mutations/usePutAcceptQuoteMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ConvertStep } from 'types/walletActions'
import { postConvert } from 'types/wallets'
import { handleOnMutationError } from 'utils/reactQuery/onMutationError'
import {
  convertFormValuesAtom,
  convertFromWalletAtom,
  convertStepAtom,
  convertToWalletAtom,
  quoteAtom,
} from '../atoms/atoms'

export const useConvertReview = () => {
  const setConvertStep = useSetAtom(convertStepAtom)
  const convertToWallet = useAtomValue(convertToWalletAtom)
  const convertFromWallet = useAtomValue(convertFromWalletAtom)
  const convertFormValues = useAtomValue(convertFormValuesAtom)
  const [quote, setQuote] = useAtom(quoteAtom)

  // API Requests
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: convertQuote, isPending: isRefreshingQuote } = usePostConvertQuoteMutation()
  const { mutate: acceptQuote, isPending: isAcceptingQuote } = usePutAcceptQuoteMutation()

  // Helper functions
  const handleRefreshQuote = () => {
    resetRequestError()

    const data: postConvert = {
      from: convertFromWallet?.currency?.code,
      to: convertToWallet?.currency?.code,
      fromWallet: convertFromWallet?.id,
      useMaximum: false,
      useMinimum: false,
      ...(convertFormValues?.reference && { reference: convertFormValues?.reference }),
      ...(!isEmpty(convertToWallet) && { toWallet: convertToWallet?.id }),
      ...(convertFormValues?.changedField === 'toAmount'
        ? { amountOut: convertFormValues?.toAmount?.toString() }
        : { amountIn: convertFormValues?.fromAmount?.toString() }),
    }

    convertQuote(data, {
      onSuccess: response => {
        setQuote(response)
      },
      onError: (error: BaseErrorResponse) => {
        handleOnMutationError(error, data, setRequestError, '/v1/quote', 'convertQuote')
      },
    })
  }

  const handleAcceptQuote = () => {
    resetRequestError()
    acceptQuote(quote?.uuid, {
      onSuccess: () => {
        setConvertStep(ConvertStep.COMPLETE)
      },
      onError: (error: BaseErrorResponse) => {
        handleOnMutationError(
          error,
          quote?.uuid,
          setRequestError,
          '/v1/quote/accept/{quoteId}',
          'acceptQuote'
        )
      },
    })
  }

  return {
    requestError,
    isRefreshingQuote,
    isAcceptingQuote,
    handleRefreshQuote,
    handleAcceptQuote,
  }
}
